import { use, useEffect, useImperativeHandle, useMemo } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { CircularProgress, Stack } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { JSONSchemaType } from 'ajv';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LanguageDropdown, PhoneNumberDropdown, SimpleRolesDropdown } from '~components';
import InputField from '~components/inputFields/InputField';
import { regexes } from '~constants';
import { findPhoneCountryByPhoneNumber } from '~constants/phoneCountries';
import { UserRoleEnum } from '~enums';
import { useFormContainer } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { BaseReference } from '~interfaces';
import i18n from '~lib/i18n';
import { Dimens } from '~themes';

import schema from './editUserSchema.json';
import UserContext from '../providers/userContext';

const baseSchema = schema as JSONSchemaType<EditUserFormFields>;

export interface EditUserFormResults {
	firstName: string;
	lastName: string;
	culture: string;
	phoneNumber?: string;
	role?: UserRoleEnum;
	department?: string;
	emailAddress?: string;
}

interface EditUserFormFields {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	language: string;
	role: UserRoleEnum;
	department: string;
	email: string;
}

interface EditUserFormProps {
	userId?: string;
	organisationId?: string | undefined | null;
	allowRoleEditing?: boolean;
	includeEmailField?: boolean;
	onSubmit: (value: EditUserFormResults) => void;
	onCancel: () => void;
}

function parsedPhoneValue(phoneNumber: string) {
	if (!phoneNumber || phoneNumber?.length === 0) {
		return { id: '', name: '' };
	}
	const country = findPhoneCountryByPhoneNumber(phoneNumber);
	if (country) {
		return {
			id: country.phone,
			name: phoneNumber.replace(`+${country.phone}`, ''),
		};
	}
	return { id: '', name: phoneNumber };
}

const EditUserForm = ({
	allowRoleEditing,
	includeEmailField,
	onSubmit,
	onCancel,
}: EditUserFormProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const userContext = use(UserContext);
	const { setDisabled, formRef } = useFormContainer();
	const { authorize } = useAuthorize();
	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);
	const inputWidth = '100%';

	const formSchema = useMemo<JSONSchemaType<EditUserFormFields>>(() => {
		if (includeEmailField) {
			return baseSchema;
		}

		const { required = [], ...schemaWithoutRequired } = baseSchema;
		const filteredRequired = required.filter(
			(field: keyof EditUserFormFields) => field !== 'email'
		);

		return {
			...schemaWithoutRequired,
			required: filteredRequired,
		} as JSONSchemaType<EditUserFormFields>;
	}, [includeEmailField]);

	const resolver = useMemo(() => ajvResolver(formSchema), [formSchema]);

	// Form control
	const { getValues, setValue, watch, control, formState } = useForm<EditUserFormFields>({
		mode: 'onChange',
		resolver,
		defaultValues: {
			firstName: '',
			lastName: '',
			phoneNumber: '',
			language: 'nl-NL',
			role: UserRoleEnum.User,
			department: '',
			email: '',
		},
	});
	const phoneNumberValue = watch('phoneNumber') ?? '';
	const phoneNumberError = useMemo(() => {
		const phoneNumber = parsedPhoneValue(phoneNumberValue).name;
		if (phoneNumber.length === 0 || phoneNumber.match(regexes.OnlyNumbers)) {
			return undefined;
		}
		return 'Phone number can only be numbers';
	}, [phoneNumberValue]);

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => {
				const formValues = getValues();
				onSubmit?.({
					firstName: formValues.firstName,
					lastName: formValues.lastName,
					phoneNumber: formValues.phoneNumber.length > 0 ? formValues.phoneNumber : undefined,
					culture: formValues.language,
					role: allowRoleEditing ? formValues.role : undefined,
					department: formValues.department.length > 0 ? formValues.department : undefined,
					emailAddress: includeEmailField ? formValues.email : undefined,
				} as EditUserFormResults);
			},
			onCancel: () => onCancel?.(),
		}),
		[allowRoleEditing, includeEmailField, onSubmit, onCancel, getValues]
	);

	const selectableUserRoles = useMemo(() => {
		return [
			UserRoleEnum.ClientAdmin,
			UserRoleEnum.User,
			...(isSuperAdmin ? [UserRoleEnum.Manufacturing, UserRoleEnum.Support] : []),
		];
	}, []);

	useEffect(() => {
		setDisabled?.(!formState.isValid || phoneNumberError != undefined);
	}, [formState.isValid, phoneNumberError, setDisabled]);

	useEffect(() => {
		if (!userContext.isLoading) {
			setValue('firstName', userContext?.user?.firstName || '');
			setValue('lastName', userContext?.user?.lastName || '');
			setValue('phoneNumber', userContext?.user?.phoneNumber || '');
			setValue('language', userContext?.user?.culture || 'nl-NL');
			setValue('role', userContext?.user?.role || UserRoleEnum.User);
			setValue('department', userContext?.user?.department || '');
			setValue('email', userContext?.user?.emailAddress || '');
		}
	}, [setValue, userContext.user, userContext.isLoading]);

	if (userContext.isLoading) {
		return (
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress sx={{ color: theme.palette.bgBrandSolid.main }} />
			</Box>
		);
	}

	return (
		<Stack spacing={Dimens.spacingLg}>
			<Controller
				name='firstName'
				control={control}
				render={({ field }) => (
					<InputField
						label={t('user.fields.firstName', { ns: 'entities' })}
						placeholder={t('user.fields.firstName', { ns: 'entities' })}
						sx={{ width: inputWidth }}
						{...field}
						required
						value={field.value}
					/>
				)}
			/>
			<Controller
				name='lastName'
				control={control}
				render={({ field }) => (
					<InputField
						label={t('user.fields.lastName', { ns: 'entities' })}
						placeholder={t('user.fields.lastName', { ns: 'entities' })}
						sx={{ width: inputWidth }}
						{...field}
						required
						value={field.value}
					/>
				)}
			/>
			{includeEmailField && (
				<Controller
					name='email'
					control={control}
					render={({ field }) => (
						<InputField
							label={t('user.fields.email', { ns: 'entities' })}
							placeholder={t('user.fields.email', { ns: 'entities' })}
							sx={{ width: inputWidth }}
							{...field}
							required
							value={field.value}
						/>
					)}
				/>
			)}
			<Controller
				name='phoneNumber'
				control={control}
				render={({ field }) => {
					const fieldValue = field.value ?? '';
					const parsedPhone = parsedPhoneValue(fieldValue);

					return (
						<PhoneNumberDropdown
							onValueChanged={(newValue?: BaseReference) => {
								if (newValue && (newValue.name?.length ?? 0) > 0) {
									field.onChange(`+${newValue.id ?? ''}${newValue.name}`);
								} else {
									field.onChange('');
								}
							}}
							sx={{ width: inputWidth }}
							error={phoneNumberError}
							{...field}
							inputValue={{
								id: parsedPhone.id,
								name: parsedPhone.name,
							}}
						/>
					);
				}}
			/>
			<Controller
				name='language'
				control={control}
				render={({ field }) => (
					<LanguageDropdown
						selectedValue={
							field.value ?
								{
									id: field.value,
									name:
										new Intl.DisplayNames(i18n.language, { type: 'language' }).of(
											field.value.split('-')[0]
										) ?? '',
								}
							:	undefined
						}
						onValueChanged={(newValue?: BaseReference) => {
							field.onChange(newValue?.id);
						}}
						sx={{ width: inputWidth }}
						{...field}
					/>
				)}
			/>
			{allowRoleEditing && (
				<Controller
					name='role'
					control={control}
					render={({ field }) => (
						<SimpleRolesDropdown
							label={t('user.fields.role', { ns: 'entities' })}
							selectedValue={field.value}
							selectableRoles={selectableUserRoles}
							onValueChanged={(newValue?: UserRoleEnum) => {
								field.onChange(newValue);
							}}
							required
							sx={{ width: inputWidth }}
							{...field}
						/>
					)}
				/>
			)}
			<Controller
				name='department'
				control={control}
				render={({ field }) => (
					<InputField
						label={t('user.fields.department', { ns: 'entities' })}
						placeholder={t('user.fields.department', { ns: 'entities' })}
						sx={{ width: inputWidth }}
						{...field}
						value={field.value}
					/>
				)}
			/>
		</Stack>
	);
};

export default EditUserForm;
